import { apiClient } from './client'
import type { Tenant } from '@/types'
import type {
  TenantAdmin,
  CreateTenantAdminPayload,
  SuperAdmin,
  CreateSuperAdminPayload,
  SuperAdminStats,
  SuperAdminHumanTask,
  SuperAdminHumanTaskDetail,
  SuperAdminCustomer,
  SuperAdminCustomerDetail,
  SystemSettings,
  UpdateSystemSettingsPayload,
  SystemConfigEntry,
  TenantConfigOverride,
  TenantInvitation,
  TenantServiceStatus,
} from '@/types/superAdmin'
import type { PaginatedResponse } from '@/types/collections'

/** Safely extract an array from a DRF response (paginated or plain list). */
function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data
  if (data != null && typeof data === 'object' && 'results' in data) {
    const results = (data as Record<string, unknown>).results
    if (Array.isArray(results)) return results
  }
  if (import.meta.env.DEV && data != null) {
    console.warn('[toArray] Unexpected API response shape, returning []:', data)
  }
  return []
}

export const superAdminAPI = {
  // Tenants
  listTenants: (): Promise<Tenant[]> =>
    apiClient.get('/tenants/').then(r => toArray<Tenant>(r.data)),
  createTenant: (data: { name: string }): Promise<Tenant> =>
    apiClient.post('/tenants/', data).then(r => r.data),
  updateTenant: (id: string, data: { is_active?: boolean; disabled_reason?: string }): Promise<Tenant> =>
    apiClient.patch(`/tenants/${id}/`, data).then(r => r.data),

  // Tenant admins
  listTenantAdmins: (tenantId: string): Promise<TenantAdmin[]> =>
    apiClient.get(`/tenants/${tenantId}/admins/`).then(r => toArray<TenantAdmin>(r.data)),
  createTenantAdmin: (tenantId: string, data: CreateTenantAdminPayload): Promise<{ detail: string }> =>
    apiClient.post(`/tenants/${tenantId}/admins/`, data).then(r => r.data),
  updateTenantAdmin: (tenantId: string, adminId: string, data: { is_active: boolean }): Promise<TenantAdmin> =>
    apiClient.patch(`/tenants/${tenantId}/admins/${adminId}/`, data).then(r => r.data),
  reassignTenantAdmin: (tenantId: string, adminId: string, newTenantId: string): Promise<TenantAdmin> =>
    apiClient.post(`/tenants/${tenantId}/admins/${adminId}/reassign/`, { new_tenant_id: newTenantId }).then(r => r.data),
  deleteTenantAdmin: (tenantId: string, adminId: string): Promise<void> =>
    apiClient.delete(`/tenants/${tenantId}/admins/${adminId}/delete/`),

  // Super admins
  listSuperAdmins: (): Promise<SuperAdmin[]> =>
    apiClient.get('/auth/super-admins/').then(r => toArray<SuperAdmin>(r.data)),
  createSuperAdmin: (data: CreateSuperAdminPayload): Promise<{ detail: string }> =>
    apiClient.post('/auth/super-admins/', data).then(r => r.data),
  updateSuperAdmin: (id: string, data: Partial<Pick<SuperAdmin, 'first_name' | 'last_name' | 'email' | 'username' | 'is_active'>>): Promise<SuperAdmin> =>
    apiClient.patch(`/auth/super-admins/${id}/`, data).then(r => r.data),
  setSuperAdminPassword: (id: string, data: { password: string; confirm_password: string }): Promise<{ detail: string }> =>
    apiClient.post(`/auth/super-admins/${id}/set-password/`, data).then(r => r.data),
  resendSuperAdminInvitation: (id: string): Promise<{ detail: string }> =>
    apiClient.post(`/auth/super-admins/${id}/resend-invitation/`).then(r => r.data),
  listTenantInvitations: (tenantId: string): Promise<TenantInvitation[]> =>
    apiClient.get(`/auth/tenants/${tenantId}/invitations/`).then(r => toArray<TenantInvitation>(r.data)),
  resendTenantInvitation: (tenantId: string, pk: string): Promise<{ detail: string }> =>
    apiClient.post(`/auth/tenants/${tenantId}/invitations/${pk}/resend/`).then(r => r.data),
  deleteSuperAdmin: (id: string): Promise<void> =>
    apiClient.delete(`/auth/super-admins/${id}/`),
  promoteSuperAdmin: (userId: string, roleCode: string = 'SUPER_ADMIN'): Promise<SuperAdmin> =>
    apiClient.post('/auth/super-admins/promote/', { user_id: userId, role: roleCode }).then(r => r.data),
  demoteSuperAdmin: (adminId: string, tenantId: string, roleCode: string = 'TENANT_ADMIN'): Promise<void> =>
    apiClient.post(`/auth/super-admins/${adminId}/demote/`, { tenant_id: tenantId, role: roleCode }).then(r => r.data),
  listTenantUsers: (tenantId: string): Promise<SuperAdmin[]> =>
    apiClient.get(`/auth/tenants/${tenantId}/users/`).then(r => toArray<SuperAdmin>(r.data)),

  // Stats
  getStats: (): Promise<SuperAdminStats> =>
    apiClient.get('/auth/super-admin/stats/').then(r => r.data),

  // Cross-tenant cases
  listAllHumanTasks: (params?: { tenant_id?: string; status?: string; search?: string; page?: number }): Promise<PaginatedResponse<SuperAdminHumanTask>> =>
    apiClient.get('/super-admin/human-tasks/', { params }).then(r => r.data),
  getHumanTaskDetail: (humanTaskId: string): Promise<SuperAdminHumanTaskDetail> =>
    apiClient.get(`/super-admin/human-tasks/${humanTaskId}/`).then(r => r.data),

  // Cross-tenant customers
  listAllCustomers: (params?: { tenant_id?: string; search?: string; page?: number; has_open_human_tasks?: string; sort_by?: string; sort_order?: string }): Promise<PaginatedResponse<SuperAdminCustomer>> =>
    apiClient.get('/super-admin/customers/', { params }).then(r => r.data),
  searchAllCustomers: (
    body: { tenant_id?: string; search?: string; has_open_human_tasks?: string; criteria?: import('@/types/marketing').FilterGroup },
    params?: { page?: number; sort_by?: string; sort_order?: string }
  ): Promise<PaginatedResponse<SuperAdminCustomer>> =>
    apiClient.post('/super-admin/customers/search/', body, { params }).then(r => r.data),
  getCustomerDetail: (customerId: string, humanTasksPage?: number): Promise<SuperAdminCustomerDetail> =>
    apiClient.get(`/super-admin/customers/${customerId}/`, {
      params: humanTasksPage ? { cases_page: humanTasksPage } : undefined
    }).then(r => r.data),

  // System settings
  getSystemSettings: (): Promise<SystemSettings> =>
    apiClient.get('/auth/system-settings/').then(r => r.data),
  updateSystemSettings: (data: UpdateSystemSettingsPayload): Promise<SystemSettings> =>
    apiClient.patch('/auth/system-settings/', data).then(r => r.data),

  // System Config
  getSystemConfigs: (category?: string): Promise<SystemConfigEntry[]> =>
    apiClient.get('/admin/system-config/', { params: category ? { category } : undefined }).then(r => toArray<SystemConfigEntry>(r.data)),
  updateSystemConfig: (key: string, value: string): Promise<SystemConfigEntry> =>
    apiClient.patch(`/admin/system-config/${key}/`, { value }).then(r => r.data),
  resetSystemConfig: (key: string): Promise<SystemConfigEntry> =>
    apiClient.post(`/admin/system-config/${key}/reset/`).then(r => r.data),

  // Tenant Config Overrides
  getTenantConfigOverrides: (tenantId: string, category?: string): Promise<TenantConfigOverride[]> =>
    apiClient.get(`/admin/system-config/tenants/${tenantId}/overrides/`, { params: category ? { category } : undefined }).then(r => toArray<TenantConfigOverride>(r.data)),
  createTenantConfigOverride: (tenantId: string, key: string, value: string): Promise<TenantConfigOverride> =>
    apiClient.post(`/admin/system-config/tenants/${tenantId}/overrides/`, { config_entry_key: key, value }).then(r => r.data),
  updateTenantConfigOverride: (tenantId: string, overrideId: string, value: string): Promise<TenantConfigOverride> =>
    apiClient.patch(`/admin/system-config/tenants/${tenantId}/overrides/${overrideId}/`, { value }).then(r => r.data),
  deleteTenantConfigOverride: (tenantId: string, overrideId: string): Promise<void> =>
    apiClient.delete(`/admin/system-config/tenants/${tenantId}/overrides/${overrideId}/`),

  // Per-key overrides (across all tenants)
  getConfigKeyOverrides: (key: string): Promise<TenantConfigOverride[]> =>
    apiClient.get(`/admin/system-config/${key}/overrides/`).then(r => toArray<TenantConfigOverride>(r.data)),

  // Tenant service status
  getTenantServiceStatus: (tenantId: string): Promise<TenantServiceStatus> =>
    apiClient.get(`/tenants/${tenantId}/service-status/`).then(r => r.data),
  getBulkServiceStatus: (): Promise<Record<string, TenantServiceStatus>> =>
    apiClient.get('/tenants/service-status/').then(r => r.data),
}
